import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import segApi, { SEG_TOKEN_KEY } from "../../lib/segApi";
import PrivacyGate from "../../components/PrivacyGate";
import { formatEuro, formatDate } from "../../lib/format";
import { Shield, LogOut, Loader2, FileText, CreditCard, Archive, MessagesSquare, User, Building2, FolderArchive, Search, X, ChevronDown } from "lucide-react";
import { SegChat } from "../../components/SegChat";
import SegPraticaDetail from "../../components/SegPraticaDetail";

const Money = ({ v, cls = "" }) => <span className={`font-mono ${cls}`}>{formatEuro(v)}</span>;

const STATO_BG = { aperta: "#0369a1", chiusa: "#047857", sospesa: "#b45309", archiviata: "#475569" };
const STATO_LABEL = { out: "Chiusa senza seguito" };
const StatoBadge = ({ stato }) => {
  const k = (stato || "").toLowerCase();
  return (
    <span data-testid="seg-area-stato-badge" style={{ backgroundColor: STATO_BG[k] || "#475569", color: "#ffffff" }}
      className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full">{STATO_LABEL[k] || stato || "—"}</span>
  );
};

function PraticaCard({ p, showCompenso = true, showMoney = true, onOpen }) {
  return (
    <div className={`rounded-lg border border-slate-800 bg-slate-900/40 p-3.5 ${onOpen ? "cursor-pointer hover:border-amber-500/60 transition-colors" : ""}`}
      onClick={onOpen ? () => onOpen(p) : undefined} role={onOpen ? "button" : undefined} data-testid={`seg-area-pratica-${p.id || p.pratica_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-amber-400 text-sm font-semibold">{p.numero || "—"}</span>
        <StatoBadge stato={p.stato} />
      </div>
      <div className="text-slate-100 text-sm mt-1 font-medium">{p.cliente || "—"}</div>
      {!showMoney && (p.data_sinistro || p.veicolo || p.targa || p.compagnia_gestionaria) && (
        <div className="mt-1.5 flex flex-col gap-0.5 text-xs" data-testid={`seg-area-info-${p.id}`}>
          {p.data_sinistro && <div><span className="text-slate-500">Data sinistro: </span><span className="text-slate-200">{formatDate(p.data_sinistro)}</span></div>}
          {(p.veicolo || p.targa) && <div><span className="text-slate-500">Veicolo: </span><span className="text-slate-200">{[p.veicolo, p.targa && <span key="t" className="font-mono">{p.targa}</span>].filter(Boolean).reduce((acc, x, i) => (i ? [...acc, " — ", x] : [x]), [])}</span></div>}
          {p.compagnia_gestionaria && <div><span className="text-slate-500">Compagnia gestionaria: </span><span className="text-slate-200">{p.compagnia_gestionaria}</span></div>}
        </div>
      )}
      {showMoney && <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
        <div><div className="text-slate-500 uppercase tracking-wide text-[10px]">Onorari</div><Money v={p.onorari} cls="text-slate-200" /></div>
        <div><div className="text-slate-500 uppercase tracking-wide text-[10px]">Spese</div><Money v={p.spese} cls="text-slate-200" /></div>
        {showCompenso && <div><div className="text-slate-500 uppercase tracking-wide text-[10px]">Compenso</div><Money v={p.compenso} cls="text-teal-400 font-semibold" /></div>}
      </div>}
      {p.data_archiviazione && <div className="text-[11px] text-slate-500 mt-2">Archiviata il {formatDate(p.data_archiviazione)}</div>}
    </div>
  );
}

const Row = ({ label, value, testId }) => (value ? (
  <div className="text-xs" data-testid={testId}><span className="text-slate-500">{label}: </span><span className="text-slate-200 break-all">{value}</span></div>
) : null);

const joinAddr = (o) => [[o.indirizzo, o.numero_civico].filter(Boolean).join(" "), [o.cap, o.comune || o.citta].filter(Boolean).join(" ") + (o.provincia ? ` (${o.provincia})` : "")].filter((x) => x && x.trim()).join(" — ");

function FoldCard({ id, title, icon: Icon, name, nameTestId, box, color, children }) {
  const key = `seg_fold_${id}`;
  const [open, setOpen] = useState(() => {
    const v = localStorage.getItem(key);
    return v === null ? window.innerWidth >= 768 : v === "1";
  });
  const toggle = () => { setOpen((o) => { localStorage.setItem(key, o ? "0" : "1"); return !o; }); };
  return (
    <div className={`rounded-lg border-2 ${box} ${open ? "p-4" : "px-4 py-2.5"}`} data-testid={`seg-area-${id}`}>
      <button type="button" onClick={toggle} className="w-full flex items-center gap-2 text-left" aria-expanded={open} data-testid={`seg-${id}-toggle`}>
        <span className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest ${color} shrink-0`}><Icon className="h-4 w-4" /> {title}</span>
        {!open && <span className="text-sm font-semibold text-slate-200 truncate">{name}</span>}
        <ChevronDown className={`h-4 w-4 ml-auto shrink-0 ${color} transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2">
          <div className="text-base font-semibold text-slate-100 mb-1.5" data-testid={nameTestId}>{name}</div>
          {children}
        </div>
      )}
    </div>
  );
}

const ProfiloCard = ({ s }) => (
  <FoldCard id="profilo" title="I tuoi dati" icon={User} name={s.nominativo} nameTestId="seg-profilo-nominativo" box="border-amber-500/50 bg-amber-500/5" color="text-amber-500">
    <div className="flex flex-col gap-y-1">
      <Row label="Email" value={s.area_email || s.email} testId="seg-profilo-email" />
      <Row label="Cellulare" value={s.cellulare} testId="seg-profilo-cellulare" />
      <Row label="Telefono" value={s.telefono} testId="seg-profilo-telefono" />
      <Row label="PEC" value={s.pec} />
      <Row label="Codice fiscale" value={s.codice_fiscale} />
      <Row label="Partita IVA" value={s.partita_iva} />
      <Row label="Indirizzo" value={joinAddr(s)} testId="seg-profilo-indirizzo" />
    </div>
  </FoldCard>
);

const StudioCard = ({ st }) => (
  <FoldCard id="studio" title="Il tuo studio di riferimento" icon={Building2} name={st.nome} nameTestId="seg-studio-nome" box="border-sky-600/50 bg-sky-600/5" color="text-sky-500">
    <div className="flex flex-col gap-y-1">
      <Row label="Indirizzo" value={joinAddr(st)} testId="seg-studio-indirizzo" />
      <Row label="Cellulare" value={st.cellulare} testId="seg-studio-cellulare" />
    </div>
  </FoldCard>
);

export default function SegnalatoreArea() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("segnalate");
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [profilo, setProfilo] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [msgUnread, setMsgUnread] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem(SEG_TOKEN_KEY)) return;
    segApi.get("/segnalatore/profilo").then(({ data }) => setProfilo(data)).catch(() => {});
    const f = () => segApi.get("/segnalatore/messaggi/unread").then(({ data }) => setMsgUnread(data.count || 0)).catch(() => {});
    f();
    const id = setInterval(f, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (tab !== "messaggi") return;
    const f = () => segApi.get("/segnalatore/messaggi").then(({ data }) => { setMsgs(data || []); setMsgUnread(0); }).catch(() => {});
    f();
    const id = setInterval(f, 20000);
    return () => clearInterval(id);
  }, [tab]);

  const sendMsg = async (text) => {
    const { data } = await segApi.post("/segnalatore/messaggi", { text });
    setMsgs((m) => [...m, data]);
  };

  useEffect(() => {
    if (!localStorage.getItem(SEG_TOKEN_KEY)) { navigate("/segnalatori", { replace: true }); return; }
    segApi.get("/segnalatore/dashboard")
      .then(({ data }) => {
        setData(data);
        if (data.tenant_id && localStorage.getItem("pgp_seg_tenant") !== data.tenant_id) {
          localStorage.setItem("pgp_seg_tenant", data.tenant_id);
          window.dispatchEvent(new Event("pgp-seg-tenant"));
        }
      })
      .catch(() => { localStorage.removeItem(SEG_TOKEN_KEY); navigate("/segnalatori", { replace: true }); })
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => { localStorage.removeItem(SEG_TOKEN_KEY); navigate("/segnalatori", { replace: true }); };

  const pratiche = data?.pratiche || [];
  const filtrate = useMemo(() => {
    const fold = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, "");
    const t = fold(q);
    if (!t) return pratiche;
    return pratiche.filter((p) => [p.cliente, p.numero, p.targa, p.veicolo, p.compagnia_gestionaria].some((v) => fold(v).includes(t)));
  }, [pratiche, q]);
  const inPagamento = useMemo(() => pratiche.filter((p) => !p.archiviato && p.ha_parcella && p.onorari_ricevuti), [pratiche]);
  const fatturate = data?.fatturate || [];
  const totCompenso = inPagamento.reduce((s, p) => s + Number(p.compenso || 0), 0);

  const tabs = [
    { v: "segnalate", l: "Pratiche segnalate", icon: FileText },
    { v: "pagamento", l: "In pagamento", icon: CreditCard },
    ...(data?.archivio_visibile ? [{ v: "archivio", l: "Archivio compensi", icon: Archive }] : []),
    ...(data?.archivio_pratiche ? [{ v: "archivio-pratiche", l: "Archivio pratiche", icon: FolderArchive }] : []),
    { v: "messaggi", l: "Messaggi allo studio", icon: MessagesSquare, badge: msgUnread },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden bg-background">
      <PrivacyGate client={segApi} base="/segnalatore/gdpr" onLogout={logout} light />
      <header className="shrink-0 z-10 border-b border-slate-800 bg-white/95 backdrop-blur px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-amber-500 flex items-center justify-center shrink-0"><Shield className="h-5 w-5 text-[#0a0d14]" strokeWidth={2.4} /></div>
            <div className="min-w-0">
              <div className="font-head font-extrabold text-sm leading-tight">Patro<span className="text-amber-500">Gest</span>Pro</div>
              <div className="text-[11px] text-slate-400 truncate" data-testid="seg-area-nome">{data?.segnalatore}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="seg-area-logout" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-amber-400 px-3 py-1.5 rounded-md hover:bg-slate-800/60"><LogOut className="h-4 w-4" /> Esci</button>
        </div>
      </header>

      <main className="flex-1 min-h-0 w-full max-w-3xl mx-auto px-4 pt-5 pb-3 overflow-y-auto md:overflow-hidden md:flex md:flex-col" data-testid="seg-area-main">
        <div className="shrink-0">
        {profilo && (
          <div className="grid md:grid-cols-2 items-start gap-3 mb-4">
            <ProfiloCard s={profilo.segnalatore} />
            <StudioCard st={profilo.studio} />
          </div>
        )}
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 mb-4 flex items-center justify-between" data-testid="seg-area-summary">
          <span className="text-sm text-slate-200">Compenso in attesa di pagamento</span>
          <Money v={totCompenso} cls="text-teal-300 text-lg font-bold" />
        </div>

        <div className="flex gap-1 border-b border-slate-800 mb-4 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`seg-area-tab-${t.v}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.v ? "border-amber-500 text-amber-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
              <t.icon className="h-4 w-4" /> {t.l}
              {t.badge > 0 && <span data-testid="seg-area-msg-unread" className="text-[10px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-1.5 py-0.5 font-semibold">{t.badge}</span>}
            </button>
          ))}
        </div>
        </div>

        <div className={`md:flex-1 md:min-h-0 pr-1 ${tab === "messaggi" ? "md:flex md:flex-col" : "md:overflow-y-auto overscroll-contain"}`} data-testid="seg-area-tab-content">
        {tab === "segnalate" && (
          <>
          <div className="relative mb-3">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per cliente, nominativo, targa o n° pratica…" data-testid="seg-area-search"
              className="w-full h-10 pl-9 pr-9 rounded-md border border-slate-700 bg-slate-900/40 text-sm text-slate-100 focus:outline-none focus:border-amber-500" />
            {q && <button type="button" onClick={() => setQ("")} data-testid="seg-area-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200"><X className="h-4 w-4" /></button>}
          </div>
          <div className="space-y-3 pb-2" data-testid="seg-area-segnalate">
            {pratiche.length === 0 ? <div className="text-center text-slate-500 text-sm py-10">Nessuna pratica segnalata.</div>
              : filtrate.length === 0 ? <div className="text-center text-slate-500 text-sm py-10" data-testid="seg-area-search-empty">Nessuna pratica trovata.</div>
              : filtrate.map((p) => <PraticaCard key={p.id} p={p} showMoney={false} onOpen={setDetail} />)}
          </div>
          </>
        )}
        {tab === "pagamento" && (
          <div className="space-y-3" data-testid="seg-area-pagamento">
            {inPagamento.length === 0 ? <div className="text-center text-slate-500 text-sm py-10">Nessun compenso in attesa di pagamento.</div>
              : inPagamento.map((p) => <PraticaCard key={p.id} p={p} />)}
          </div>
        )}
        {tab === "archivio" && data?.archivio_visibile && (
          <div className="space-y-3" data-testid="seg-area-archivio">
            {fatturate.length === 0 ? <div className="text-center text-slate-500 text-sm py-10">Nessun compenso archiviato.</div>
              : fatturate.map((f) => <PraticaCard key={f.id} p={f} />)}
          </div>
        )}
        {tab === "archivio-pratiche" && data?.archivio_pratiche && (
          <div className="space-y-3" data-testid="seg-area-archivio-pratiche">
            {data.archivio_pratiche.length === 0 ? <div className="text-center text-slate-500 text-sm py-10">Nessuna pratica chiusa o archiviata.</div>
              : data.archivio_pratiche.map((p) => <PraticaCard key={p.id} p={p} showMoney={false} />)}
          </div>
        )}
        {tab === "messaggi" && (
          <div className="rounded-lg border border-slate-800 h-[65dvh] md:h-auto md:flex-1 md:min-h-0 flex flex-col overflow-hidden" style={{ backgroundColor: "#ffffff" }} data-testid="seg-area-messaggi">
            <SegChat messages={msgs} mine="segnalatore" onSend={sendMsg} testPrefix="seg-area-chat" light />
          </div>
        )}
        </div>
      </main>
      <SegPraticaDetail p={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
